<template>
  <div class="due-date-offset-field" :data-testid="dataTestid">
    <span v-if="label" class="due-date-offset-field__label">{{ label }}</span>
    <div class="due-date-offset-field__row">
      <v-text-field
        :value="magnitudeText"
        type="number"
        min="0"
        :max="maxMagnitude"
        step="1"
        outlined
        dense
        hide-details
        class="due-date-offset-field__magnitude"
        :error="!!shownError"
        :disabled="disabled"
        data-testid="due-date-offset-field__magnitude"
        @keydown.native="blockNonIntegerKeys"
        @paste.native="onPaste"
        @input="onMagnitude"
      />
      <v-select
        :value="direction"
        :items="directions"
        outlined
        dense
        hide-details
        class="due-date-offset-field__direction"
        :disabled="disabled"
        data-testid="due-date-offset-field__direction"
        @input="onDirection"
      />
    </div>
    <p
      v-if="shownError"
      class="due-date-offset-field__error"
      role="alert"
      data-testid="due-date-offset-field__error"
    >
      {{ shownError }}
    </p>
  </div>
</template>

<script>
import { NOTIFICATION_FORM_COPY } from '../../../utils/constants';
import {
  fromSignedOffset,
  OFFSET_MAGNITUDE_MAX,
  parseOffsetMagnitude,
  toSignedOffset,
} from '../../../utils/notificationNameUtils';

const BLOCKED_KEYS = ['-', '+', 'e', 'E', '.'];

export default {
  name: 'DueDateOffsetField',
  props: {
    value: { type: Number, default: 0 },
    label: { type: String, default: '' },
    error: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    dataTestid: { type: String, default: 'due-date-offset-field' },
  },
  data() {
    return {
      directions: [
        { text: 'Before', value: 'before' },
        { text: 'After', value: 'after' },
      ],
      selectedDirection: 'before',
      magnitudeText: '0',
      clientError: '',
      maxMagnitude: OFFSET_MAGNITUDE_MAX,
      reverting: false,
    };
  },
  computed: {
    parsed() {
      return fromSignedOffset(this.value);
    },
    direction() {
      if (!this.parsed.magnitude) return this.selectedDirection;
      return this.parsed.direction;
    },
    shownError() {
      return this.error || this.clientError;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(next) {
        if (next == null || next === '') return;
        const parsed = fromSignedOffset(next);
        this.magnitudeText = String(parsed.magnitude);
        if (parsed.magnitude !== 0) {
          this.selectedDirection = parsed.direction;
        }
      },
    },
  },
  beforeDestroy() {
    this.selectedDirection = 'before';
    this.magnitudeText = '0';
    this.clientError = '';
    this.reverting = false;
  },
  methods: {
    blockNonIntegerKeys(event) {
      if (BLOCKED_KEYS.includes(event.key)) event.preventDefault();
    },
    onPaste(event) {
      const text = event.clipboardData ? event.clipboardData.getData('text') : '';
      if (!/^\d+$/.test(String(text).trim())) event.preventDefault();
    },
    validate() {
      const parsed = parseOffsetMagnitude(this.magnitudeText);
      this.clientError = parsed.error || '';
      this.$emit('valid', !parsed.error);
      return !parsed.error;
    },
    revertMagnitude(previous) {
      const fallback = parseOffsetMagnitude(previous).error ? '' : previous;
      this.reverting = true;
      this.magnitudeText = null;
      this.$nextTick(() => {
        this.magnitudeText = fallback;
        this.$nextTick(() => {
          this.reverting = false;
        });
      });
    },
    emitSigned(magnitude, direction) {
      this.selectedDirection = direction;
      const signed = toSignedOffset(magnitude, direction);
      this.$emit('input', signed);
      this.$emit('valid', true);
    },
    onMagnitude(raw) {
      if (this.reverting) return;
      const text = raw == null ? '' : String(raw).trim();
      if (text === '') {
        this.magnitudeText = '';
        this.clientError = NOTIFICATION_FORM_COPY.OFFSET_REQUIRED;
        this.$emit('input', null);
        this.$emit('valid', false);
        return;
      }
      const parsed = parseOffsetMagnitude(text);
      if (parsed.error) {
        const previous = this.magnitudeText;
        this.clientError = parsed.error;
        this.$emit('valid', false);
        this.revertMagnitude(previous);
        return;
      }
      const direction = this.direction;
      this.clientError = '';
      this.magnitudeText = String(parsed.magnitude);
      this.emitSigned(parsed.magnitude, direction);
    },
    onDirection(next) {
      const parsed = parseOffsetMagnitude(this.magnitudeText);
      this.selectedDirection = next || 'before';
      if (parsed.error) {
        this.clientError = parsed.error;
        this.$emit('valid', false);
        return;
      }
      this.clientError = '';
      this.emitSigned(parsed.magnitude, this.selectedDirection);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './DueDateOffsetField.scss';
</style>
